import { ClinicalGuides, GenerationMode } from '../App';
import { AIDentistAnalysis } from '../types';
import { getToken } from './authApiClient';

const API_BASE_URL = (import.meta.env.VITE_API_URL || '') + '/api';

const getAuthHeaders = () => {
    const token = getToken();
    const headers: HeadersInit = { 'Content-Type': 'application/json' };
    if (token) {
        headers['Authorization'] = `Bearer ${token}`;
    }
    return headers;
};

// Validation Types
export interface ValidationChecks {
    fullFaceVisible: boolean;
    eyesVisible: boolean;
    smileWithOpenMouth: boolean;
    appropriateDistance: boolean;
}

export class ValidationError extends Error {
    errors: string[];
    checks: ValidationChecks | null;

    constructor(message: string, errors: string[], checks: ValidationChecks | null) {
        super(message);
        this.name = 'ValidationError';
        this.errors = errors;
        this.checks = checks;
    }
}

async function handleResponse<T>(response: Response): Promise<T> {
    if (!response.ok) {
        let errorMessage = 'An unknown error occurred on the server.';
        let validationData: { validationErrors?: string[], checks?: ValidationChecks } | null = null;

        try {
            const contentType = response.headers.get('content-type');
            if (contentType && contentType.includes('application/json')) {
                const errorData = await response.json();
                errorMessage = errorData.message || errorMessage;

                // Check for validation structure
                if (response.status === 400 && (errorData.validationErrors || errorData.checks)) {
                    validationData = errorData;
                }
            } else {
                const textError = await response.text();
                errorMessage = textError || `Server error: ${response.status} ${response.statusText}`;
            }
        } catch (parseError) {
            errorMessage = `Server error: ${response.status} ${response.statusText}`;
        }

        // Throw specific validation error if data exists
        if (validationData && validationData.validationErrors) {
            throw new ValidationError(errorMessage, validationData.validationErrors, validationData.checks || null);
        }

        // Special handling for authentication errors
        if (response.status === 401) {
            errorMessage = 'Authentication required. Please log in again.';
        } else if (response.status === 403) {
            errorMessage = 'Access forbidden. Your session may have expired. Please log in again.';
        }

        throw new Error(errorMessage);
    }
    return await response.json();
}

export async function generateAIDentistAnalysis(
    base64ImageData: string,
    mimeType: string
): Promise<AIDentistAnalysis> {
    const response = await fetch(`${API_BASE_URL}/generate-ai-dentist-analysis`, {
        method: 'POST',
        credentials: 'include',
        headers: getAuthHeaders(),
        body: JSON.stringify({ base64ImageData, mimeType }),
    });
    return handleResponse<AIDentistAnalysis>(response);
}

export async function upscaleAndEnhanceImage(
    base64ImageData: string,
    mimeType: string
): Promise<{ base64: string; mimeType: string }> {
    const response = await fetch(`${API_BASE_URL}/upscale-and-enhance-image`, {
        method: 'POST',
        credentials: 'include',
        headers: getAuthHeaders(),
        body: JSON.stringify({ base64ImageData, mimeType }),
    });
    return handleResponse<{ base64: string; mimeType: string }>(response);
}

export async function generateShowcasePortrait(
    base64ImageData: string,
    mimeType: string,
    _onProgress: (message: string) => void // onProgress is no longer used directly here
): Promise<{ base64: string; mimeType: string }> {
    const response = await fetch(`${API_BASE_URL}/generate-showcase-portrait`, {
        method: 'POST',
        credentials: 'include',
        headers: getAuthHeaders(),
        body: JSON.stringify({ base64ImageData, mimeType }),
    });
    return handleResponse<{ base64: string; mimeType: string }>(response);
}

export async function generateDentalModel(
    base64ImageData: string,
    mimeType: string,
    _onProgress: (message: string) => void // onProgress is no longer used directly here
): Promise<string> {
    const response = await fetch(`${API_BASE_URL}/generate-dental-model`, {
        method: 'POST',
        credentials: 'include',
        headers: getAuthHeaders(),
        body: JSON.stringify({ base64ImageData, mimeType }),
    });
    const result = await handleResponse<{ base64: string }>(response);
    return result.base64;
}

export async function generateCaseReport(
    treatmentType: string,
    smileRating: object,
    afterSmileRating: object | null,
    treatmentPlan: object | null,
    stylePreset: string | null
): Promise<any> {
    const response = await fetch(`${API_BASE_URL}/generate-case-report`, {
        method: 'POST',
        credentials: 'include',
        headers: getAuthHeaders(),
        body: JSON.stringify({ treatmentType, smileRating, afterSmileRating, treatmentPlan, stylePreset }),
    });
    return handleResponse<any>(response);
}

export async function generateSmileRating(
    base64ImageData: string,
    mimeType: string,
    caseId?: number
): Promise<object> {
    const response = await fetch(`${API_BASE_URL}/generate-smile-rating`, {
        method: 'POST',
        credentials: 'include',
        headers: getAuthHeaders(),
        body: JSON.stringify({ base64ImageData, mimeType, caseId }),
    });
    return handleResponse<object>(response);
}

export async function generateSmileAnalysis(
    base64ImageData: string,
    mimeType: string,
    caseId?: number
): Promise<object> {
    const response = await fetch(`${API_BASE_URL}/generate-smile-analysis`, {
        method: 'POST',
        credentials: 'include',
        headers: getAuthHeaders(),
        body: JSON.stringify({ base64ImageData, mimeType, caseId }),
    });
    return handleResponse<object>(response);
}

export async function generateRestorativeAnalysis(
    base64ImageData: string,
    mimeType: string,
    caseId?: number
): Promise<object> {
    const response = await fetch(`${API_BASE_URL}/generate-restorative-analysis`, {
        method: 'POST',
        credentials: 'include',
        headers: getAuthHeaders(),
        body: JSON.stringify({ base64ImageData, mimeType, caseId }),
    });
    return handleResponse<object>(response);
}


// Helper function for Async Queue Polling with Exponential Backoff and Cancellation Support
async function pollJob<T>(
    jobId: number,
    signal?: AbortSignal,
    onProgress?: (message: string) => void
): Promise<T> {
    const BASE_INTERVAL = 5000; // 5 seconds
    const MAX_INTERVAL = 30000; // 30 seconds max backoff
    const MAX_ATTEMPTS = 360; // ~30 minutes with backoff
    const MAX_CONSECUTIVE_ERRORS = 5;
    let attempts = 0;
    let currentInterval = BASE_INTERVAL;
    let consecutiveErrors = 0;

    while (attempts < MAX_ATTEMPTS) {
        // Check for cancellation before each poll
        if (signal?.aborted) {
            throw new Error('Generation cancelled');
        }

        attempts++;
        try {
            const response = await fetch(`${API_BASE_URL}/v2/jobs/${jobId}`, {
                method: 'GET',
                credentials: 'include',
                headers: getAuthHeaders(),
                signal,
            });

            if (response.status === 429) {
                // Rate limited - apply exponential backoff
                consecutiveErrors++;
                currentInterval = Math.min(currentInterval * 1.5, MAX_INTERVAL);
                console.warn(`[Generation] Rate limited (attempt ${attempts}), backing off to ${currentInterval}ms`);
            } else if (response.status === 401) {
                throw new Error('Unauthorized');
            } else if (response.ok) {
                // Success - reset backoff
                consecutiveErrors = 0;
                currentInterval = BASE_INTERVAL;

                const job = await response.json();

                // UX: Update progress message if queue position is available
                if (job.status === 'pending' && job.queuePosition && onProgress) {
                    onProgress(`Current Queue Position: ${job.queuePosition}. Processing shortly...`);
                } else if (job.status === 'processing' && onProgress) {
                    onProgress('Processing your smile design...');
                }

                if (job.status === 'completed') {
                    if (job.result) return job.result as T;
                    // Fallback if result is missing but status says completed
                    throw new Error('Job completed but returned no result');
                } else if (job.status === 'failed') {
                    throw new Error(job.error || 'Async generation failed');
                }
                // Status is 'pending' or 'processing' - continue polling
            } else {
                // Other errors - apply backoff
                consecutiveErrors++;
                if (consecutiveErrors > MAX_CONSECUTIVE_ERRORS) {
                    currentInterval = Math.min(currentInterval * 1.2, MAX_INTERVAL);
                }
            }
        } catch (e: any) {
            // Re-throw abort errors immediately
            if (e?.name === 'AbortError' || signal?.aborted) {
                throw new Error('Generation cancelled');
            }
            console.warn(`[Generation] Polling error (attempt ${attempts}):`, e?.message || e);
            if (e?.message === 'Unauthorized') throw e;
            consecutiveErrors++;
            if (consecutiveErrors > MAX_CONSECUTIVE_ERRORS) {
                throw new Error('Connection to server lost. Please try again.');
            }
        }

        // Check for cancellation before waiting
        if (signal?.aborted) {
            throw new Error('Generation cancelled');
        }

        // Wait before next poll
        await new Promise(resolve => setTimeout(resolve, currentInterval));
    }
    throw new Error('Generation timed out. Please try again.');
}

export async function generateSmileDesign(
    base64ImageData: string,
    mimeType: string,
    customPrompt: string,
    selectedStyles: string[],
    generationMode: GenerationMode,
    treatmentType: 'cosmetic' | 'orthodontic' | 'restorative' | 'pro-automated' | 'fast-pro',
    treatmentPlanJson: string,
    gumLinePath: { x: number; y: number }[] | null,
    clinicalGuides: ClinicalGuides | null,
    caseId?: number,
    caseTitle?: string,
    caseDescription?: string,
    caseParams?: any,
    originalImageBase64?: string,
    onProgress?: (message: string) => void
): Promise<{ images: { imageData: string, textResponse: string | null }[]; caseId?: number }> {
    // Add timeout and retry logic for better reliability
    const controller = new AbortController();
    // Extended timeout to cover polling duration (30 mins)
    const timeoutId = setTimeout(() => controller.abort(), 1800000);

    try {
        const response = await fetch(`${API_BASE_URL}/generate-smile-design`, {
            method: 'POST',
            credentials: 'include',
            headers: getAuthHeaders(),
            signal: controller.signal,
            body: JSON.stringify({
                base64ImageData,
                mimeType,
                customPrompt,
                selectedStyles,
                generationMode,
                treatmentType,
                treatmentPlanJson,
                gumLinePath,
                clinicalGuides,
                caseId,
                caseTitle,
                caseDescription,
                caseParams,
                originalImageBase64
            }),
        });

        clearTimeout(timeoutId);

        // ASYNC HANDLING
        if (response.status === 202) {
            const data = await response.json();
            if (data.jobId) {
                return await pollJob<{ images: { imageData: string, textResponse: string | null }[]; caseId?: number }>(data.jobId, controller.signal, onProgress);
            }
        }

        return handleResponse<{ images: { imageData: string, textResponse: string | null }[]; caseId?: number }>(response);
    } catch (error) {
        clearTimeout(timeoutId);
        if (error instanceof Error && error.name === 'AbortError') {
            throw new Error('Request timed out. Please try again with a smaller image or different settings.');
        }
        throw error;
    }
}


export async function generateSmileVideo(
    base64ImageData: string,
    mimeType: string,
    _onProgress: (message: string) => void, // onProgress is no longer used directly here
    aspectRatio?: string,
    caseId?: number
): Promise<string> {
    const response = await fetch(`${API_BASE_URL}/generate-smile-video`, {
        method: 'POST',
        credentials: 'include',
        headers: getAuthHeaders(),
        body: JSON.stringify({ base64ImageData, mimeType, aspectRatio, caseId }),
    });
    const result = await handleResponse<{ videoUrl: string }>(response);
    return result.videoUrl;
}

/**
 * V2 Smile Design Generation
 * Uses advanced AI model for enhanced smile design.
 * Supports Cosmetic, Orthodontic, and Restorative treatment types.
 * Supports Unique (1 image) and Creatives (3 images) output modes.
 */
// ... existing code ...
export async function generateSmileDesignV2(
    base64ImageData: string,
    mimeType: string,
    treatmentType: 'cosmetic' | 'orthodontic' | 'restorative',
    outputMode: 'unique' | 'creatives',
    hasMouthGuide: boolean,
    hasGumLineGuide: boolean,
    caseId?: number,
    caseTitle?: string,
    caseDescription?: string,
    caseParams?: any,
    originalImageBase64?: string,
    cosmeticStyle: 'natural' | 'hollywood' = 'natural',
    onProgress?: (message: string) => void
): Promise<{ images: { imageData: string, textResponse: string | null }[]; caseId?: number; creditsRemaining?: number }> {
    // Add timeout for long-running V2 requests
    const controller = new AbortController();
    // Extended timeout for polling (30 mins)
    const timeoutId = setTimeout(() => controller.abort(), 1800000);

    try {
        const response = await fetch(`${API_BASE_URL}/v2/generate-smile-design`, {
            method: 'POST',
            credentials: 'include',
            headers: getAuthHeaders(),
            signal: controller.signal,
            body: JSON.stringify({
                base64ImageData,
                mimeType,
                treatmentType,
                outputMode,
                hasMouthGuide,
                hasGumLineGuide,
                caseId,
                caseTitle,
                caseDescription,
                caseParams,
                originalImageBase64,
                cosmeticStyle
            }),
        });

        clearTimeout(timeoutId);

        // ASYNC HANDLING
        if (response.status === 202) {
            const data = await response.json();
            if (data.jobId) {
                return await pollJob<{ images: { imageData: string, textResponse: string | null }[]; caseId?: number; creditsRemaining?: number }>(data.jobId, controller.signal, onProgress);
            }
        }

        return handleResponse<{ images: { imageData: string, textResponse: string | null }[]; caseId?: number; creditsRemaining?: number }>(response);
    } catch (error) {
        clearTimeout(timeoutId);
        if (error instanceof Error && error.name === 'AbortError') {
            throw new Error('V2 request timed out. Please try again with a smaller image or different settings.');
        }
        throw error;
    }
}

export async function generateEliteCover(
    base64ImageData: string,
    mimeType: string,
    caseId?: number,
    aspectRatio: string = '16:9',
    accentColor: string = '#14b8a6'
): Promise<{ imageUrl: string }> {
    const response = await fetch(`${API_BASE_URL}/generate-elite-cover`, {
        method: 'POST',
        credentials: 'include',
        headers: getAuthHeaders(),
        body: JSON.stringify({ base64ImageData, mimeType, caseId, aspectRatio, accentColor }),
    });
    const data = await handleResponse<{ base64?: string; mimeType?: string; imageUrl?: string }>(response);

    if (data.imageUrl) {
        return { imageUrl: data.imageUrl };
    }
    if (data.base64 && data.mimeType) {
        return { imageUrl: `data:${data.mimeType};base64,${data.base64}` };
    }
    throw new Error("Failed to retrieve elite cover image data.");
}